import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";

import { functions } from "../../Security/firebase-config";
import { useTeam } from "../../context/TeamContext";
import { useAccess } from "../../context/AccessContext";
import {
  SUBSCRIPTION_PLANS,
  SUBSCRIPTION_PROMO_CONFIG,
  STORAGE_ADD_ON_OPTIONS,
} from "../../config/subscriptionPlans";
import { FUNCTION_NAMES } from "../../utils/functionNames";
import { buildTeamScopedPath, getRequestedTeamId } from "../../utils/teamRouteTarget";

import Footer from "../../components/core-components/Footer";
import TeamCenterFrame from "../../components/team-center/TeamCenterFrame";

import "./TeamSetupBilling.css";

const isDesktopShell = typeof window !== "undefined" && !!window.desktopAPI;
const PLAN_ORDER = ["basic", "premier", "collegiate", "pro"];
const PLAN_CHANGE_PRIORITY = ["free", "basic", "premier", "collegiate", "pro"];
const FREE_TRIAL_DAYS = 7;
const FULL_TEAM_MODE_THRESHOLD = 5;
const FULL_TEAM_MODE_PRICE_CAP_USERS = 5;

const PLAN_COPY = {
  basic: {
    badge: "Core Plan",
    headline: "Access all core tools except VOD Reviewer",
    kicker:
      "Basic stays standard per-user pricing and does not enter full-team-mode.",
    storageHero: "No Included Cloud Storage",
    uploadHero: "Core Access Only",
    features: {
      seats: "Up to 10 Members",
      pricingMode: "Standard per-user only",
      fullTeamMode: "No",
      storage: "0 GB Included",
      upload: "Core Access Only",
      vod: "No",
      stats: "Yes",
      calendar: "Yes",
      prepCenter: "Included",
      notes: "Yes",
      lfg: "Yes",
      overage: "Not available",
    },
  },

  premier: {
    badge: "Most Popular",
    headline: "Full access plan for most active teams",
    kicker:
      "At 5 or more team members, pricing caps at 5× the per-user plan price.",
    storageHero: "200 GB Cloud Storage",
    uploadHero: "36 GB Teeam / Month",
    features: {
      seats: "Up to 10 Members",
      pricingMode: "Per-user → capped at 5 users",
      fullTeamMode: "Yes, at 5+ members",
      storage: "200 GB Included",
      upload: "36 GB Team / month",
      vod: "Yes",
      stats: "Yes",
      calendar: "Yes",
      prepCenter: "Included",
      notes: "Yes",
      lfg: "Yes",
      overage: "Not available",
    },
  },

  collegiate: {
    badge: "Higher Upload Limit",
    headline: "Full access with more monthly upload capacity",
    kicker:
      "At 5 or more team members, pricing caps at 5× the per-user plan price.",
    storageHero: "200 GB Cloud Storage",
    uploadHero: "60 GB Team / Month",
    features: {
      seats: "Up to 10 Members",
      pricingMode: "Per-user → capped at 5 users",
      fullTeamMode: "Yes, at 5+ members",
      storage: "200 GB Included",
      upload: "60 GB Team / month",
      vod: "Yes",
      stats: "Yes",
      calendar: "Yes",
      prepCenter: "Included",
      notes: "Yes",
      lfg: "Yes",
      overage: "Not available",
    },
  },

  pro: {
    badge: "No Hard Upload Cap",
    headline:
      "Full access plan with included high-volume uploads and overage billing",
    kicker:
      "At 5 or more team members, pricing caps at 5× the per-user plan price. Uploads continue above 200 GB and overage is billed next cycle.",
    storageHero: "200 GB Cloud Storage",
    uploadHero: "No Hard Upload Cap",
    features: {
      seats: "Up to 10 Members",
      pricingMode: "Per-user → capped at 5 users",
      fullTeamMode: "Yes, at 5+ members",
      storage: "200 GB Included",
      upload: "200 GB Team included, then overage",
      vod: "Yes",
      stats: "Yes",
      calendar: "Yes",
      prepCenter: "Included",
      notes: "Yes",
      lfg: "Yes",
      overage: "Billed next cycle",
    },
    overageMetaByCurrency: {
      CAD: 1.5,
      USD: 1.2,
      EUR: 1.0,
      GBP: 1.0,
    },
  },
};

const FULL_TEAM_MODE_ROLE_LIMITS = {
  player: 5,
  coach: 1,
  analyst: 1,
  manager: 1,
  sub: 2,
};

const ROLE_ACCESS_COPY = {
  player:
    "Players receive full access to the subscription features, including guides, calendar, availability, VOD tools, notes, and team tools available on the selected plan.",
  coach:
    "Coaches receive full access to the subscription features and are intended for team staff focused on preparation, review, and oversight.",
  analyst:
    "Analysts receive full access to the subscription features and are intended for staff handling review, analysis, and preparation.",
  manager:
    "Managers have elevated team-management permissions similar to the team owner, such as moderating members and updating roles. They also keep full access to calendar, availability, and guide library tools, but have limited monthly egress allowances.",
  sub:
    "Subs are substitute players. They keep full access to calendar, availability, and guide library tools, but have limited monthly egress allowances compared with core full-access roles.",
};

const FEATURE_ROWS = [
  { key: "badge", label: "Plan Type" },
  { key: "headline", label: "Best For" },
  { key: "seats", label: "Roster Limits" },
  { key: "pricingMode", label: "Pricing" },
  { key: "fullTeamMode", label: "Full-Team-Mode" },
  { key: "storage", label: "Cloud Storage" },
  { key: "upload", label: "Upload Limit" },
  { key: "vod", label: "VOD Reviewer" },
  { key: "stats", label: "Team Stats" },
  { key: "calendar", label: "Calendar" },
  { key: "prepCenter", label: "Prep Center" },
  { key: "notes", label: "Notes / Highlights / Favorites" },
  { key: "lfg", label: "LFG Access" },
  { key: "overage", label: "Upload Overage" },
];

function buildBillingCenterPath(teamId) {
  if (isDesktopShell) {
    const params = new URLSearchParams();
    params.set("tab", "billing");
    if (teamId) {
      params.set("teamId", teamId);
    }
    return `/settings?${params.toString()}`;
  }

  return buildTeamScopedPath("/billing", teamId);
}

function normalizePendingChangeTiming(value) {
  return value === "immediate_prorated" ? "immediate_prorated" : "next_cycle";
}

function getPlanPriority(planKey) {
  const normalizedPlanKey =
    typeof planKey === "string" ? planKey.trim().toLowerCase() : "";
  const resolvedIndex = PLAN_CHANGE_PRIORITY.indexOf(normalizedPlanKey);
  return resolvedIndex >= 0 ? resolvedIndex : -1;
}

function getResolvedStorageTotalGb(planKey, storageAddOnKey) {
  const normalizedPlanKey =
    typeof planKey === "string" ? planKey.trim().toLowerCase() : "";
  const plan = SUBSCRIPTION_PLANS[normalizedPlanKey];
  const baseStorageGb =
    typeof plan?.storageGb === "number" ? plan.storageGb : 0;

  if (!normalizedPlanKey || normalizedPlanKey === "basic") {
    return baseStorageGb;
  }

  const matchedAddOn = STORAGE_ADD_ON_OPTIONS.find(
    (option) => option.key === storageAddOnKey
  );

  if (typeof matchedAddOn?.totalStorageGb === "number") {
    return matchedAddOn.totalStorageGb;
  }

  return baseStorageGb;
}

function resolvePendingPlanChangeTiming({
  currentPlanKey,
  currentStorageAddOnKey,
  selectedPlanKey,
  selectedStorageAddOnKey,
}) {
  const normalizedCurrentPlanKey =
    typeof currentPlanKey === "string" ? currentPlanKey.trim().toLowerCase() : "";
  const normalizedSelectedPlanKey =
    typeof selectedPlanKey === "string" ? selectedPlanKey.trim().toLowerCase() : "";

  if (!normalizedSelectedPlanKey) {
    return "next_cycle";
  }

  const currentPriority = getPlanPriority(normalizedCurrentPlanKey);
  const selectedPriority = getPlanPriority(normalizedSelectedPlanKey);

  if (currentPriority < 0 && selectedPriority >= 0) {
    return "immediate_prorated";
  }

  if (selectedPriority > currentPriority) {
    return "immediate_prorated";
  }

  if (selectedPriority < currentPriority) {
    return "next_cycle";
  }

  const currentStorageTotalGb = getResolvedStorageTotalGb(
    normalizedCurrentPlanKey,
    normalizedCurrentPlanKey === "basic" ? "" : currentStorageAddOnKey || ""
  );
  const selectedStorageTotalGb = getResolvedStorageTotalGb(
    normalizedSelectedPlanKey,
    normalizedSelectedPlanKey === "basic" ? "" : selectedStorageAddOnKey || ""
  );

  if (selectedStorageTotalGb > currentStorageTotalGb) {
    return "immediate_prorated";
  }

  return "next_cycle";
}

const TeamSetupBilling = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { teamId: contextTeamId } = useTeam();
  const { refreshAccess } = useAccess() || {};
  const requestedTeamId = getRequestedTeamId(location.search);
  const teamId = requestedTeamId || contextTeamId;

  const [teamMemberCount, setTeamMemberCount] = useState(0);
  const [selectedStorageAddOnKey, setSelectedStorageAddOnKey] = useState("");
  const [selectedPlanKey, setSelectedPlanKey] = useState("");
  const [selectedBillingInterval, setSelectedBillingInterval] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [pageError, setPageError] = useState("");
  const [loadingTeamMeta, setLoadingTeamMeta] = useState(true);
  const [teamBillingView, setTeamBillingView] = useState(null);
  const billingData = teamBillingView?.billingData || null;
  const billingSummary = teamBillingView?.billingSummary || null;
  const activeTeamSubscription = billingData || null;
  const teamBillingTimeZone =
    typeof teamBillingView?.teamData?.teamTimeZone === "string" &&
    teamBillingView.teamData.teamTimeZone.trim().length > 0
      ? teamBillingView.teamData.teamTimeZone.trim()
      : undefined;

  const teamCapabilities = teamBillingView?.capabilities || {};
  const canEditBillingPlan = teamCapabilities.canEditBillingPlan === true;
  const isTeamLeader = teamBillingView?.isTeamLeader === true;
  const currentUid = teamBillingView?.currentUid || null;

  const selectablePlans = useMemo(() => {
    return PLAN_ORDER.filter((planKey) => planKey in SUBSCRIPTION_PLANS).map(
      (planKey) => [planKey, SUBSCRIPTION_PLANS[planKey]]
    );
  }, []);

  const hasPendingNextCycleChange =
    activeTeamSubscription?.pendingNextCycleChange === true;
  const pendingChangeTiming = normalizePendingChangeTiming(
    activeTeamSubscription?.pendingChangeTiming ||
      billingSummary?.pendingChangeTiming
  );
  const hasImmediatePendingChange =
    hasPendingNextCycleChange && pendingChangeTiming === "immediate_prorated";

  const currentPlanKey =
    typeof activeTeamSubscription?.planKey === "string"
      ? activeTeamSubscription.planKey
      : "";

  const currentBillingInterval =
    typeof activeTeamSubscription?.billingInterval === "string"
      ? activeTeamSubscription.billingInterval
      : "";

  const currentStorageAddOnKey =
    typeof activeTeamSubscription?.selectedStorageAddOnKey === "string"
      ? activeTeamSubscription.selectedStorageAddOnKey
      : "";

  const nextCyclePlanKey =
    typeof activeTeamSubscription?.nextCyclePlanKey === "string"
      ? activeTeamSubscription.nextCyclePlanKey
      : "";

  const nextCycleBillingInterval =
    typeof activeTeamSubscription?.nextCycleBillingInterval === "string"
      ? activeTeamSubscription.nextCycleBillingInterval
      : "";

  const nextCycleSelectedStorageAddOnKey =
    typeof activeTeamSubscription?.nextCycleSelectedStorageAddOnKey === "string"
      ? activeTeamSubscription.nextCycleSelectedStorageAddOnKey
      : "";

  const activeSubscriptionStatus =
    typeof activeTeamSubscription?.status === "string"
      ? activeTeamSubscription.status.trim().toLowerCase()
      : "";

  const isSubscriptionActuallyActive =
    activeSubscriptionStatus === "active" ||
    activeSubscriptionStatus === "trialing";

  const matchesCurrentLivePlan =
    !!selectedPlanKey &&
    selectedPlanKey === currentPlanKey &&
    (selectedBillingInterval || "") === (currentBillingInterval || "") &&
    ((selectedPlanKey === "basic" ? "" : selectedStorageAddOnKey || "") ===
      (currentPlanKey === "basic" ? "" : currentStorageAddOnKey || ""));

  const matchesQueuedNextCyclePlan =
    !!selectedPlanKey &&
    hasPendingNextCycleChange === true &&
    selectedPlanKey === nextCyclePlanKey &&
    (selectedBillingInterval || "") === (nextCycleBillingInterval || "") &&
    ((selectedPlanKey === "basic" ? "" : selectedStorageAddOnKey || "") ===
      (nextCyclePlanKey === "basic" ? "" : nextCycleSelectedStorageAddOnKey || ""));

  const isSamePlanSelection =
    matchesCurrentLivePlan && hasPendingNextCycleChange !== true;

  const isSavedButNotYetLiveSelection =
    matchesCurrentLivePlan && !isSubscriptionActuallyActive;
  const selectedChangeTiming = resolvePendingPlanChangeTiming({
    currentPlanKey,
    currentStorageAddOnKey,
    selectedPlanKey,
    selectedStorageAddOnKey,
  });

  const isUnchangedSelection =
    isSamePlanSelection ||
    isSavedButNotYetLiveSelection ||
    matchesQueuedNextCyclePlan;

  const promoAwarePricingPreview =
    billingSummary?.pricingPreview &&
    typeof billingSummary.pricingPreview === "object"
      ? billingSummary.pricingPreview
      : null;
  const savedPlanKey = billingSummary?.planKey || billingData?.planKey || "";
  const savedBillingInterval =
    billingSummary?.billingInterval || billingData?.billingInterval || "";
  const savedSelectedStorageAddOnKey =
    selectedPlanKey === "basic"
      ? ""
      : billingSummary?.selectedStorageAddOnKey ||
        billingData?.selectedStorageAddOnKey ||
        "";
  const currentSelectedStorageKey =
    selectedPlanKey === "basic" ? "" : selectedStorageAddOnKey || "";

  const selectionMatchesSavedBillingPreview =
    !!selectedPlanKey &&
    selectedPlanKey === savedPlanKey &&
    (selectedBillingInterval || "") === savedBillingInterval &&
    currentSelectedStorageKey === savedSelectedStorageAddOnKey;

  const selectedPlanPromoPreview = selectionMatchesSavedBillingPreview
    ? promoAwarePricingPreview
    : null;
  const selectedPlanTeamPromos = Array.isArray(selectedPlanPromoPreview?.appliedTeamPromos)
    ? selectedPlanPromoPreview.appliedTeamPromos
    : [];
  const selectedPlanUserPromos =
    currentUid &&
    selectedPlanPromoPreview?.appliedUserPromosByPayer &&
    typeof selectedPlanPromoPreview.appliedUserPromosByPayer === "object" &&
    Array.isArray(selectedPlanPromoPreview.appliedUserPromosByPayer?.[currentUid])
      ? selectedPlanPromoPreview.appliedUserPromosByPayer[currentUid]
      : [];

  const setupTimingText = hasPendingNextCycleChange
    ? hasImmediatePendingChange
      ? "This approved upgrade is configured to activate immediately once all selected payers have approved and every payer card is ready. Only the prorated difference is charged before the next automatic renewal date."
      : "Any downgrade, cancellation, or payer-structure change saved here will apply to the next billing cycle."
    : isSavedButNotYetLiveSelection
      ? "This subscription plan is already saved for the team. The team owner still needs to finish payer setup before the subscription can start."
      : isSamePlanSelection
        ? "This team is already on this live subscription setup."
      : isSubscriptionActuallyActive
        ? selectedChangeTiming === "immediate_prorated"
          ? "This upgrade is configured to activate immediately once all selected payers have approved and every payer card is ready. Only the prorated difference is charged before the next automatic renewal date."
          : "This billing change will apply to the next billing cycle after the current renewal date."
        : "This subscription setup will activate immediately once payer approval and payment setup are complete.";
  const selectedPlan = selectedPlanKey ? SUBSCRIPTION_PLANS[selectedPlanKey] : null;
  const selectedCopy = selectedPlanKey ? PLAN_COPY[selectedPlanKey] : null;

  const isLaunchBasicPromoActive =
    SUBSCRIPTION_PROMO_CONFIG.launchBasicFallbackEnabled === true &&
    SUBSCRIPTION_PROMO_CONFIG.launchBasicSelectableWithoutBilling === true;

  const SUPPORTED_CURRENCIES = ["CAD", "USD", "EUR", "GBP"];

  const InfoTooltip = ({ text }) => (
    <span className="---TeamSetupBilling-tooltip" tabIndex="0">
      ?
      <span className="---TeamSetupBilling-tooltip-bubble">{text}</span>
    </span>
  );

  const detectDisplayCurrency = () => {
    const subscriptionCurrency = activeTeamSubscription?.billingCurrency;

    if (
      typeof subscriptionCurrency === "string" &&
      SUPPORTED_CURRENCIES.includes(subscriptionCurrency.toUpperCase())
    ) {
      return subscriptionCurrency.toUpperCase();
    }

    return "CAD";
  };

  const displayCurrency = detectDisplayCurrency();

  const formatMoneyByCurrency = (valueByCurrency) => {
    const value = valueByCurrency?.[displayCurrency];

    if (typeof value !== "number") {
      return "—";
    }

    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: displayCurrency,
      currencyDisplay: "code",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatPerUnitByCurrency = (valueByCurrency, suffix = "") => {
    const value = valueByCurrency?.[displayCurrency];

    if (typeof value !== "number") {
      return "—";
    }

    const formatted = new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: displayCurrency,
      currencyDisplay: "code",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);

    return `${formatted}${suffix}`;
  };

  const formatPerUserPrice = (planKey) => {
    const plan = SUBSCRIPTION_PLANS[planKey];
    const pricing = plan?.pricing || {};

    return formatMoneyByCurrency({
      CAD: pricing.cadPerUserMonthly,
      USD: pricing.usdPerUserMonthly,
      EUR: pricing.eurPerUserMonthly,
      GBP: pricing.gbpPerUserMonthly,
    });
  };

  const getPlanDisplayPrice = (planKey) => {
    const plan = SUBSCRIPTION_PLANS[planKey];

    if (!plan) {
      return {
        priceMain: "",
        priceSuffix: "",
        originalPrice: "",
        originalPriceSuffix: "",
        savingsText: "",
      };
    }

    if (planKey === "basic" && isLaunchBasicPromoActive) {
      return {
        priceMain: "FREE",
        priceSuffix: "during launch",
        originalPrice: formatMoneyByCurrency({
          CAD: 3.99,
          USD: 2.99,
          EUR: 2.69,
          GBP: 2.29,
        }),
        originalPriceSuffix: "/ user / month",
        savingsText: "Launch Promo • Free",
      };
    }

    return {
      priceMain: formatPerUserPrice(planKey),
      priceSuffix: "/ user / month",
      originalPrice: "",
      originalPriceSuffix: "",
      savingsText: "",
    };
  };

  const getSelectedStorageAddOn = () => {
    return (
      STORAGE_ADD_ON_OPTIONS.find((option) => option.key === selectedStorageAddOnKey) || null
    );
  };

  const getPlanUnitPriceNumber = (planKey) => {
    if (planKey === "basic" && isLaunchBasicPromoActive) {
      return 0;
    }

    const pricing = SUBSCRIPTION_PLANS[planKey]?.pricing || {};

    switch (displayCurrency) {
      case "USD":
        return pricing.usdPerUserMonthly ?? pricing.cadPerUserMonthly ?? null;
      case "EUR":
        return pricing.eurPerUserMonthly ?? pricing.cadPerUserMonthly ?? null;
      case "GBP":
        return pricing.gbpPerUserMonthly ?? pricing.cadPerUserMonthly ?? null;
      case "CAD":
      default:
        return pricing.cadPerUserMonthly ?? null;
    }
  };

  const getPlanSupportsFullTeamMode = (planKey) => {
    return SUBSCRIPTION_PLANS?.[planKey]?.supportsFullTeamMode === true;
  };

  const renderRoleLimits = (planKey, maxMembers) => {
    if (!getPlanSupportsFullTeamMode(planKey)) {
      return `Up to ${maxMembers} Members`;
    }

    return (
      <div className="---TeamSetupBilling-role-list">
        <div className="---TeamSetupBilling-role-item">
          <span className="---TeamSetupBilling-role-name">
            Player
            <InfoTooltip text={ROLE_ACCESS_COPY.player} />
          </span>
          <span className="---TeamSetupBilling-role-value">
            {FULL_TEAM_MODE_ROLE_LIMITS.player}
          </span>
        </div>

        <div className="---TeamSetupBilling-role-item">
          <span className="---TeamSetupBilling-role-name">
            Coach
            <InfoTooltip text={ROLE_ACCESS_COPY.coach} />
          </span>
          <span className="---TeamSetupBilling-role-value">
            {FULL_TEAM_MODE_ROLE_LIMITS.coach}
          </span>
        </div>

        <div className="---TeamSetupBilling-role-item">
          <span className="---TeamSetupBilling-role-name">
            Analyst
            <InfoTooltip text={ROLE_ACCESS_COPY.analyst} />
          </span>
          <span className="---TeamSetupBilling-role-value">
            {FULL_TEAM_MODE_ROLE_LIMITS.analyst}
          </span>
        </div>

        <div className="---TeamSetupBilling-role-item">
          <span className="---TeamSetupBilling-role-name">
            Manager
            <InfoTooltip text={ROLE_ACCESS_COPY.manager} />
          </span>
          <span className="---TeamSetupBilling-role-value">
            {FULL_TEAM_MODE_ROLE_LIMITS.manager}
          </span>
        </div>

        <div className="---TeamSetupBilling-role-item">
          <span className="---TeamSetupBilling-role-name">
            Sub
            <InfoTooltip text={ROLE_ACCESS_COPY.sub} />
          </span>
          <span className="---TeamSetupBilling-role-value">
            {FULL_TEAM_MODE_ROLE_LIMITS.sub}
          </span>
        </div>
      </div>
    );
  };

  const getPricingPreview = (planKey) => {
    const unitPrice = getPlanUnitPriceNumber(planKey);

    if (typeof unitPrice !== "number") {
      return {
        modeLabel: "Pricing unavailable",
        detailLabel: "",
        monthlyTotalFormatted: "—",
      };
    }

    const supportsFullTeamMode = getPlanSupportsFullTeamMode(planKey);
    const fullTeamModeActive =
      supportsFullTeamMode && teamMemberCount >= FULL_TEAM_MODE_THRESHOLD;

    const billableSeatCount = fullTeamModeActive
      ? Math.min(teamMemberCount, FULL_TEAM_MODE_PRICE_CAP_USERS)
      : teamMemberCount;

    const total = unitPrice * billableSeatCount;

    let modeLabel = "Per-user pricing";
    let detailLabel = `${billableSeatCount} billable user${billableSeatCount === 1 ? "" : "s"}`;

    if (fullTeamModeActive) {
      modeLabel = "Full-team-mode pricing";
      detailLabel = `Capped at ${FULL_TEAM_MODE_PRICE_CAP_USERS} billable users`;
    }

    return {
      modeLabel,
      detailLabel,
      monthlyTotalFormatted: new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: displayCurrency,
        currencyDisplay: "code",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(total),
    };
  };

  const trialEligibility =
    activeTeamSubscription?.trialEligibility ||
    billingSummary?.trialEligibility ||
    null;

  const isFreeTrialEligible = useMemo(() => {
    if (typeof trialEligibility?.eligible === "boolean") {
      return trialEligibility.eligible;
    }

    const normalizedSubscriptionStatus = String(
      billingSummary?.status || activeTeamSubscription?.status || ""
    ).trim().toLowerCase();
    const trialAlreadyConsumed =
      activeTeamSubscription?.trialConsumedByTeam === true ||
      billingSummary?.trialConsumedByTeam === true;

    if (
      trialAlreadyConsumed ||
      normalizedSubscriptionStatus === "trialing" ||
      normalizedSubscriptionStatus === "active" ||
      normalizedSubscriptionStatus === "past_due"
    ) {
      return false;
    }

    if (typeof activeTeamSubscription?.hasUsedTrial === "boolean") {
      return !activeTeamSubscription.hasUsedTrial;
    }

    if (typeof billingSummary?.hasUsedTrial === "boolean") {
      return !billingSummary.hasUsedTrial;
    }

    // Do not advertise a free trial until the selected team's billing data confirms eligibility.
    return false;
  }, [activeTeamSubscription, billingSummary, trialEligibility]);

  const trialDeniedMessage =
    !isFreeTrialEligible && typeof trialEligibility?.userMessage === "string"
      ? trialEligibility.userMessage
      : "";
  const shouldShowTrialBanner = !loadingTeamMeta && isFreeTrialEligible;
  const hasBillingContextNotice =
    !loadingTeamMeta && (!isTeamLeader || Boolean(trialDeniedMessage));

  const formatBillingDate = (value) => {
    if (!value) return "Not scheduled yet";

    const d =
      typeof value?.toDate === "function"
        ? value.toDate()
        : typeof value?._seconds === "number"
          ? new Date(value._seconds * 1000)
          : typeof value?.seconds === "number"
            ? new Date(value.seconds * 1000)
        : typeof value === "number"
          ? new Date(value * 1000)
          : value instanceof Date
            ? value
            : typeof value === "string"
              ? new Date(value)
              : null;

    if (!d || Number.isNaN(d.getTime())) {
      return "Not scheduled yet";
    }

    const options = {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZoneName: "short",
    };

    if (teamBillingTimeZone) {
      options.timeZone = teamBillingTimeZone;
    }

    try {
      return d.toLocaleString(undefined, options);
    } catch (error) {
      const fallbackOptions = { ...options };
      delete fallbackOptions.timeZone;
      return d.toLocaleString(undefined, fallbackOptions);
    }
  };

  const currentPlanLabel =
    currentPlanKey && SUBSCRIPTION_PLANS[currentPlanKey]?.label
      ? SUBSCRIPTION_PLANS[currentPlanKey].label
      : currentPlanKey || "this plan";

  const isTrialingSubscription = activeSubscriptionStatus === "trialing";
  const isActivePaidSubscription = activeSubscriptionStatus === "active";

  const billingStartText = isTrialingSubscription
    ? !matchesCurrentLivePlan
      ? `Your team is currently on the ${currentPlanLabel} free trial. Saving this selection ${
          selectedChangeTiming === "immediate_prorated"
            ? "prepares it to activate immediately with proration once payer approvals and cards are complete."
            : "queues it for the next billing cycle."
        }`
      : activeTeamSubscription?.trialEndsAt
        ? `Your team is currently on the ${currentPlanLabel} free trial. Paid billing begins when the trial ends on ${formatBillingDate(activeTeamSubscription.trialEndsAt)}.`
        : `Your team is currently on the ${currentPlanLabel} free trial. Paid billing begins when the trial ends.`
    : isActivePaidSubscription && !hasPendingNextCycleChange
      ? matchesCurrentLivePlan
        ? `Your team is currently active on ${currentPlanLabel}.`
        : `Your team is currently active on ${currentPlanLabel}. Saving this selection ${
            selectedChangeTiming === "immediate_prorated"
              ? "activates it immediately with proration once payer approvals and cards are complete."
              : "queues it for the next billing cycle."
          }`
      : hasPendingNextCycleChange
        ? activeTeamSubscription?.currentPeriodEnd
          ? hasImmediatePendingChange
            ? `This approved upgrade will activate before the current renewal on ${formatBillingDate(activeTeamSubscription.currentPeriodEnd)} once approvals and cards are fully complete.`
            : `This change will apply on ${formatBillingDate(activeTeamSubscription.currentPeriodEnd)}.`
          : hasImmediatePendingChange
            ? "This approved upgrade will activate immediately with proration once approvals and cards are fully complete."
            : "This change will apply at the next billing cycle once that date is available."
        : isFreeTrialEligible
          ? "The free trial will start once all selected payers approve and complete payment setup. Paid billing will begin after the free trial ends."
          : "Paid billing will begin once all selected payers approve and complete payment setup.";

  useEffect(() => {
    let cancelled = false;

    async function loadTeamMeta() {
      if (!teamId) {
        if (!cancelled) {
          setTeamMemberCount(0);
          setTeamBillingView(null);
          setLoadingTeamMeta(false);
        }
        return;
      }

      try {
        setLoadingTeamMeta(true);
        setPageError("");
        const loadBillingSetup = httpsCallable(
          functions,
          FUNCTION_NAMES.getTeamBillingSetup
        );
        const result = await loadBillingSetup({ teamId });
        const data = result?.data || {};

        if (cancelled) return;

        const teamData = data.teamData || {};
        const members = Array.isArray(teamData.members) ? teamData.members : [];
        setTeamBillingView(data);
        setTeamMemberCount(members.length);
      } catch (error) {
        console.error("TeamSetupBilling team meta load error:", error);
        if (!cancelled) {
          setTeamBillingView(null);
          setTeamMemberCount(0);
          setPageError(error?.message || "Failed to load team billing view.");
        }
      } finally {
        if (!cancelled) {
          setLoadingTeamMeta(false);
        }
      }
    }

    loadTeamMeta();

    return () => {
      cancelled = true;
    };
  }, [teamId]);

  useEffect(() => {
    const initialPlanKey =
      typeof activeTeamSubscription?.planKey === "string"
        ? activeTeamSubscription.planKey
        : "";

    setSelectedPlanKey(initialPlanKey);

    if (initialPlanKey && SUBSCRIPTION_PLANS[initialPlanKey]) {
      const plan = SUBSCRIPTION_PLANS[initialPlanKey];
      const validIntervals = Array.isArray(plan.billingIntervals)
        ? plan.billingIntervals
        : [];

      const initialInterval =
        validIntervals.includes(activeTeamSubscription?.billingInterval)
          ? activeTeamSubscription.billingInterval
          : validIntervals[0] || "";

      setSelectedBillingInterval(initialInterval);
    } else {
      setSelectedBillingInterval("");
    }

    const initialStorageAddOnKey =
      typeof activeTeamSubscription?.selectedStorageAddOnKey === "string"
        ? activeTeamSubscription.selectedStorageAddOnKey
        : "";

    setSelectedStorageAddOnKey(initialStorageAddOnKey);
  }, [activeTeamSubscription]);

  useEffect(() => {
    if (!selectedPlanKey || !SUBSCRIPTION_PLANS[selectedPlanKey]) {
      setSelectedBillingInterval("");
      setSelectedStorageAddOnKey("");
      return;
    }

    const plan = SUBSCRIPTION_PLANS[selectedPlanKey];
    const validIntervals = Array.isArray(plan.billingIntervals)
      ? plan.billingIntervals
      : [];

    if (!validIntervals.includes(selectedBillingInterval)) {
      setSelectedBillingInterval(validIntervals[0] || "");
    }

    if (selectedPlanKey === "basic" && selectedStorageAddOnKey) {
      setSelectedStorageAddOnKey("");
    }
  }, [selectedPlanKey, selectedBillingInterval, selectedStorageAddOnKey]);

  const formatIntervalLabel = (interval) => {
    switch (interval) {
      case "monthly":
        return "Monthly";
      case "every_4_months":
        return "Every 4 Months";
      case "every_8_months":
        return "Every 8 Months";
      default:
        return interval || "Not set";
    }
  };

  const handleSponsorRedirect = () => {
    navigate("/feedback?category=Subscription%20Plan");
  };

  const handleContinue = async () => {
    setPageError("");

    if (!FUNCTION_NAMES.selectTeamPlan) {
      setPageError("selectTeamPlan function is not configured.");
      return;
    }

    if (!teamId) {
      setPageError("No active team selected.");
      return;
    }

    if (!canEditBillingPlan) {
      setPageError("Only the team owner can change the subscription plan.");
      return;
    }

    if (!selectedPlanKey) {
      setPageError("Please choose a plan.");
      return;
    }

    const plan = SUBSCRIPTION_PLANS[selectedPlanKey];
    const validIntervals = Array.isArray(plan?.billingIntervals)
      ? plan.billingIntervals
      : [];

    if (validIntervals.length > 0 && !selectedBillingInterval) {
      setPageError("Please choose a billing interval.");
      return;
    }

    if (selectedPlanKey === "basic" && selectedStorageAddOnKey) {
      setPageError("Basic plan cannot include a storage add-on.");
      return;
    }

    if (isUnchangedSelection) {
      setPageError(
        hasPendingNextCycleChange && matchesQueuedNextCyclePlan
          ? hasImmediatePendingChange
            ? "This exact subscription setup is already pending immediate activation once payer approvals and cards are complete."
            : "This exact subscription setup is already queued for the next billing cycle."
          : "This team already has this subscription setup. Choose a different plan, interval, or storage add-on to make a billing change."
      );
      return;
    }

    try {
      setSubmitting(true);

      const selectTeamPlan = httpsCallable(functions, FUNCTION_NAMES.selectTeamPlan);

      await selectTeamPlan({
        teamId,
        planKey: selectedPlanKey,
        billingInterval: selectedBillingInterval || null,
        selectedStorageAddOnKey:
          selectedPlanKey === "basic" ? null : selectedStorageAddOnKey || null,
      });

      if (typeof refreshAccess === "function") {
        await refreshAccess();
      }

      navigate(buildTeamScopedPath("/team-setup-payers", teamId));
    } catch (error) {
      console.error("TeamSetupBilling selectTeamPlan error:", error);
      setPageError(error?.message || "Failed to save the team plan.");
    } finally {
      setSubmitting(false);
    }
  };

  const selectedIntervalOptions =
    selectedPlan && Array.isArray(selectedPlan.billingIntervals)
      ? selectedPlan.billingIntervals
      : [];

  const selectedPlanUsesLaunchPromoFreePath =
    selectedPlanKey === "basic" && isLaunchBasicPromoActive;

  const selectedStorageAddOn = getSelectedStorageAddOn();
  const selectedPlanPricingPreview = selectedPlanKey
    ? getPricingPreview(selectedPlanKey)
    : null;

  const continueButtonLabel = submitting
    ? "Saving Plan..."
    : matchesQueuedNextCyclePlan
      ? hasImmediatePendingChange
        ? "Pending Immediate Activation"
        : "Queued For Next Cycle"
      : isSavedButNotYetLiveSelection
        ? "Current Setup"
        : isSamePlanSelection
          ? "Already Active"
          : isSubscriptionActuallyActive
            ? selectedChangeTiming === "immediate_prorated"
              ? "Save Immediate Upgrade"
              : "Save For Next Billing Cycle"
            : selectedPlanUsesLaunchPromoFreePath
              ? "Continue with Launch Basic"
              : "Save Plan";

  const renderPlanHeaderCell = (planKey, plan) => {
    const selected = selectedPlanKey === planKey;
    const displayPrice = getPlanDisplayPrice(planKey);
    const isCurrentActivePlanCard =
      isSubscriptionActuallyActive && planKey === currentPlanKey;
    const isQueuedNextCyclePlanCard =
      hasPendingNextCycleChange && planKey === nextCyclePlanKey;
    const selectButtonLabel = selected
      ? isSubscriptionActuallyActive
        ? matchesQueuedNextCyclePlan
          ? hasImmediatePendingChange
            ? "Selected for immediate activation"
            : "Selected for next billing cycle"
          : isCurrentActivePlanCard
            ? "Current Plan"
            : selectedChangeTiming === "immediate_prorated"
              ? "Selected for immediate activation"
              : "Selected for next billing cycle"
        : "Selected"
      : isSubscriptionActuallyActive
        ? isCurrentActivePlanCard
          ? "Keep current plan"
          : "Choose for next billing cycle"
        : "Choose Plan";

    return (
      <th key={planKey} className={`---TeamSetupBilling-plan-head ${selected ? "---TeamSetupBilling-plan-head--selected" : ""}`}>
        <div className="---TeamSetupBilling-plan-head-inner">
          <div className="---TeamSetupBilling-plan-badge">{PLAN_COPY[planKey].badge}</div>
          <div className="---TeamSetupBilling-plan-name">{plan.label}</div>
          <div className="---TeamSetupBilling-plan-price">{displayPrice.priceMain}</div>
          <div className="---TeamSetupBilling-plan-price-sub">{displayPrice.priceSuffix}</div>
          {displayPrice.originalPrice ? (
            <div className="---TeamSetupBilling-plan-old-price">
              {displayPrice.originalPrice} {displayPrice.originalPriceSuffix}
            </div>
          ) : null}
          {displayPrice.savingsText ? (
            <div className="---TeamSetupBilling-plan-savings">{displayPrice.savingsText}</div>
          ) : null}
          {isCurrentActivePlanCard ? (
            <div className="---TeamSetupBilling-plan-state-badge">
              Currently active subscription
            </div>
          ) : null}
          {isQueuedNextCyclePlanCard && !isCurrentActivePlanCard ? (
            <div className="---TeamSetupBilling-plan-state-badge ---TeamSetupBilling-plan-state-badge--queued">
              {hasImmediatePendingChange
                ? "Pending immediate activation"
                : "Queued for next billing cycle"}
            </div>
          ) : null}

          <button
            type="button"
            className={`---TeamSetupBilling-select-button ${selected ? "---TeamSetupBilling-select-button--selected" : ""}`}
            onClick={() => {
              if (!canEditBillingPlan) return;
              setSelectedPlanKey(planKey);
            }}
            disabled={!canEditBillingPlan}
          >
            {selectButtonLabel}
          </button>
        </div>
      </th>
    );
  };

  const getRowValue = (rowKey, planKey, plan) => {
    const copy = PLAN_COPY[planKey];

    switch (rowKey) {
      case "badge":
        return copy.badge;
      case "headline":
        return copy.headline;
      case "seats":
        return renderRoleLimits(planKey, plan?.maxMembers);
      case "pricingMode":
        return copy.features.pricingMode;
      case "fullTeamMode":
        return (
          <>
            {copy.features.fullTeamMode}
            {getPlanSupportsFullTeamMode(planKey) ? (
              <>
                {" "}
                <InfoTooltip text="Full-Team-Mode activates once the team reaches 5 players. At that point, the team unlocks space for 1 Coach, 1 Analyst, 1 Manager, and up to 2 Subs. Coach, Analyst, and Player roles receive full access to the subscription features. Manager and Sub roles have limited monthly egress allowances, but still retain full access to the team calendar, availability tools, and guide library. The Manager role also receives elevated team-management permissions, such as moderating members and updating roles. To prevent abuse, roles cannot be repeatedly switched throughout the month." />
              </>
            ) : null}
          </>
        );
      case "storage":
        return selectedStorageAddOn && planKey !== "basic"
          ? `${selectedStorageAddOn.totalStorageGb} GB Included`
          : copy.features.storage;
      case "upload":
        return copy.features.upload;
      case "vod":
        return copy.features.vod;
      case "stats":
        return copy.features.stats;
      case "calendar":
        return copy.features.calendar;
      case "prepCenter":
        return copy.features.prepCenter;
      case "notes":
        return copy.features.notes;
      case "lfg":
        return copy.features.lfg;
      case "overage":
        return planKey === "pro" && copy.overageMetaByCurrency
          ? `Yes — ${formatPerUnitByCurrency(copy.overageMetaByCurrency, " per GB")}`
          : copy.features.overage;
      default:
        return "—";
    }
  };

  const renderSummaryPanel = () => (
    <aside className="---TeamSetupBilling-summary">
      <div className="---TeamSetupBilling-summary-card">
        <div className="---TeamSetupBilling-summary-kicker">
          {isSubscriptionActuallyActive && !matchesCurrentLivePlan
            ? "Next-cycle selection"
            : "Current selection"}
        </div>

        <div className="---TeamSetupBilling-summary-plan">
          {selectedPlan ? selectedPlan.label : "Select a plan"}
        </div>

        <p className="---TeamSetupBilling-summary-copy">
          {selectedCopy
            ? selectedCopy.headline
            : "Choose a plan from the comparison table below."}
        </p>

        <div className="---TeamSetupBilling-note-box">
          {billingStartText}
        </div>

        {!canEditBillingPlan ? (
          <div className="---TeamSetupBilling-note-box">
            View-only. Only the team owner can change the subscription plan for this team.
          </div>
        ) : null}

        {selectedPlan ? (
          <>
            <div className="---TeamSetupBilling-summary-grid">
              <div className="---TeamSetupBilling-summary-stat">
                <span className="---TeamSetupBilling-summary-stat-label">Roster Limits</span>
                  <strong className="---TeamSetupBilling-summary-stat-value">
                    {getPlanSupportsFullTeamMode(selectedPlanKey)
                      ? "5 Players + 1 Coach + 1 Analyst + 1 Manager + 2 Subs"
                      : `Up to ${selectedPlan.maxMembers} Members`}
                  </strong>
              </div>

              <div className="---TeamSetupBilling-summary-stat">
                <span className="---TeamSetupBilling-summary-stat-label">Team Size</span>
                <strong className="---TeamSetupBilling-summary-stat-value">
                  {teamMemberCount} Current Member{teamMemberCount === 1 ? "" : "s"}
                </strong>
              </div>

              <div className="---TeamSetupBilling-summary-stat">
                <span className="---TeamSetupBilling-summary-stat-label">Pricing Mode</span>
                <strong className="---TeamSetupBilling-summary-stat-value">
                  {selectedPlanPricingPreview?.modeLabel || "—"}
                </strong>
              </div>

              <div className="---TeamSetupBilling-summary-stat">
                <span className="---TeamSetupBilling-summary-stat-label">Estimated Monthly</span>
                <strong className="---TeamSetupBilling-summary-stat-value">
                  {selectedPlanPricingPreview?.monthlyTotalFormatted || "—"}
                </strong>
              </div>
            </div>

            {selectedPlanPromoPreview &&
            (selectedPlanTeamPromos.length > 0 || selectedPlanUserPromos.length > 0) ? (
              <div className="---TeamSetupBilling-note-box">
                {typeof selectedPlanPromoPreview.teamBillPromoDiscountAmountMinor === "number" &&
                selectedPlanPromoPreview.teamBillPromoDiscountAmountMinor > 0 ? (
                  <div>
                    Team discount:{" "}
                    {new Intl.NumberFormat(undefined, {
                      style: "currency",
                      currency: displayCurrency,
                      currencyDisplay: "code",
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(
                      selectedPlanPromoPreview.teamBillPromoDiscountAmountMinor / 100
                    )}{" "}
                    off per month on the saved setup.
                  </div>
                ) : null}
                {selectedPlanTeamPromos.map((promo) => (
                  <div key={`billing-team-promo-${promo.code}`}>
                    {promo.displayName || promo.code}:{" "}
                    {promo.discountType === "percent"
                      ? `${promo.discountPercent || 0}% off`
                      : typeof promo.fixedAmountMinor === "number"
                        ? new Intl.NumberFormat(undefined, {
                            style: "currency",
                            currency: promo.fixedAmountCurrency || displayCurrency,
                            currencyDisplay: "code",
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }).format(promo.fixedAmountMinor / 100)
                        : "Configured discount"}
                    {promo.summary ? ` • ${promo.summary}` : ""}
                    {typeof promo.remainingCycles === "number"
                      ? ` • ${promo.remainingCycles} billing cycle${
                          promo.remainingCycles === 1 ? "" : "s"
                        } remaining`
                      : promo.durationMode === "forever"
                        ? " • No cycle limit"
                        : ""}
                    {promo.activeUntil
                      ? ` • Active until ${formatBillingDate(promo.activeUntil)}`
                      : ""}
                  </div>
                ))}
                {selectedPlanUserPromos.length > 0 ? (
                  <div>Your payer-specific discounts will be shown in full on the payer setup step.</div>
                ) : null}
              </div>
            ) : null}

            <div className="---TeamSetupBilling-control-group">
              <label
                className="---TeamSetupBilling-field-label"
                htmlFor="---TeamSetupBilling-billing-interval"
              >
                Billing interval
              </label>

              {selectedIntervalOptions.length > 1 ? (
                <select
                  id="---TeamSetupBilling-billing-interval"
                  className="---TeamSetupBilling-select"
                  value={selectedBillingInterval}
                  onChange={(e) => setSelectedBillingInterval(e.target.value)}
                  disabled={!canEditBillingPlan}
                >
                  {selectedIntervalOptions.map((interval) => (
                    <option key={interval} value={interval}>
                      {formatIntervalLabel(interval)}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="---TeamSetupBilling-static-value">
                  {formatIntervalLabel(
                    selectedPlan.billingIntervals?.[0] ||
                      selectedBillingInterval ||
                      "monthly"
                  )}
                </div>
              )}
            </div>

            {selectedPlanKey && selectedPlanKey !== "basic" ? (
              <div className="---TeamSetupBilling-control-group">
                <label
                  className="---TeamSetupBilling-field-label"
                  htmlFor="---TeamSetupBilling-storage-addon"
                >
                  Cloud storage increase add-on
                </label>

                <select
                  id="---TeamSetupBilling-storage-addon"
                  className="---TeamSetupBilling-select"
                  value={selectedStorageAddOnKey}
                  onChange={(e) => setSelectedStorageAddOnKey(e.target.value)}
                  disabled={!canEditBillingPlan}
                >
                  <option value="">No storage increase</option>
                  {STORAGE_ADD_ON_OPTIONS.map((option) => (
                    <option key={option.key} value={option.key}>
                      {option.totalStorageGb} GB total — {formatMoneyByCurrency({
                        CAD: option.pricing.cadMonthly,
                        USD: option.pricing.usdMonthly,
                        EUR: option.pricing.eurMonthly,
                        GBP: option.pricing.gbpMonthly,
                      })} / month
                    </option>
                  ))}
                </select>

                {selectedStorageAddOn ? (
                  <div className="---TeamSetupBilling-note-box">
                    Total storage becomes {selectedStorageAddOn.totalStorageGb} GB.
                  </div>
                ) : null}
              </div>
            ) : null}

            {selectedPlanUsesLaunchPromoFreePath ? (
              <div className="---TeamSetupBilling-note">
                Launch promotion active. This Basic plan can be activated without billing setup while the promotion is enabled.
              </div>
            ) : getPlanSupportsFullTeamMode(selectedPlanKey) ? (
              <div className="---TeamSetupBilling-note">
                For {selectedPlan.label}, pricing stays standard per-user while your team has fewer than {FULL_TEAM_MODE_THRESHOLD} players. Once your team reaches {FULL_TEAM_MODE_THRESHOLD} players, Full-Team-Mode activates: billing caps at {FULL_TEAM_MODE_PRICE_CAP_USERS} billable users, and the roster structure expands to allow 1 Coach, 1 Analyst, 1 Manager, and up to 2 Subs.
              </div>
            ) : isFreeTrialEligible ? (
              <div className="---TeamSetupBilling-note">
                You will not be charged when you continue. Billing starts only after the {FREE_TRIAL_DAYS}-day trial ends.
              </div>
            ) : (
              <div className="---TeamSetupBilling-note">
                Next, you’ll choose who pays and how the payment is split.
              </div>
            )}

            {selectedPlanPricingPreview?.detailLabel ? (
              <div className="---TeamSetupBilling-note">
                {selectedPlanPricingPreview.detailLabel}. Prices shown in {displayCurrency}.
              </div>
            ) : null}
          </>
        ) : null}

        <div className="---TeamSetupBilling-action-row">
          <button
            type="button"
            className="---TeamSetupBilling-button"
            onClick={handleContinue}
            disabled={!canEditBillingPlan || submitting || isUnchangedSelection}
          >
            {canEditBillingPlan ? continueButtonLabel : "View Only"}
          </button>
        </div>
      </div>
    </aside>
  );

  return (
    <TeamCenterFrame
      title="Team Subscription"
      identityLabel="Team Subscription"
      showPageTitle={false}
      showTeamFacts
      description={setupTimingText}
      actions={(
        <button
          type="button"
          onClick={() => navigate(buildBillingCenterPath(teamId))}
        >
          Billing Center
        </button>
      )}
      footer={<Footer />}
    >
      <div className="---TeamSetupBilling-page TeamCenterBillingPage">
        <div className="---TeamSetupBilling-shell">
        {hasBillingContextNotice ? (
          <section className="---TeamSetupBilling-hero ---TeamSetupBilling-context">
            {!isTeamLeader ? (
              <div className="---TeamSetupBilling-note-box">
                You can review this team billing page, but only the team owner can choose or save the subscription plan and complete the team subscription process.
              </div>
            ) : null}
            {trialDeniedMessage ? (
              <div className="---TeamSetupBilling-note-box">
                {trialDeniedMessage}
              </div>
            ) : null}
          </section>
        ) : null}

        <section className="---TeamSetupBilling-access-card" aria-labelledby="team-center-access-heading">
          <div className="---TeamSetupBilling-access-copy">
            <span>Team Center access</span>
            <h2 id="team-center-access-heading">Know what needs a subscription</h2>
            <p>These tools use different access rules so your team can keep essential organization available before billing is active.</p>
          </div>
          <dl className="---TeamSetupBilling-access-grid">
            <div>
              <dt>Roster Expectations</dt>
              <dd>Available to active team members without a subscription.</dd>
            </div>
            <div>
              <dt>My Routine</dt>
              <dd>Available to every signed-in account. No team or subscription is required.</dd>
            </div>
          </dl>
        </section>

        {pageError ? <div className="---TeamSetupBilling-error">{pageError}</div> : null}

        <div className={`---TeamSetupBilling-introRow${shouldShowTrialBanner ? "" : " ---TeamSetupBilling-introRow--single"}`}>
          {shouldShowTrialBanner ? (
            <section className="---TeamSetupBilling-trial-banner">
              <div className="---TeamSetupBilling-trial-pill">Try It Free</div>
              <div className="---TeamSetupBilling-trial-copy">
                No payment today — start with a {FREE_TRIAL_DAYS}-day FREE TRIAL.
              </div>
            </section>
          ) : null}

          <section className="---TeamSetupBilling-special-panel">
            <div className="---TeamSetupBilling-special-copy">
              <h2 className="---TeamSetupBilling-special-title">
                Need sponsorship or a custom plan?
              </h2>
              <p className="---TeamSetupBilling-special-text">
                Want to be sponsored or have a special plan in mind? Reach out here and we’ll review it with you.
              </p>
            </div>

            <button
              type="button"
              className="---TeamSetupBilling-button ---TeamSetupBilling-button--secondary"
              onClick={handleSponsorRedirect}
            >
              Contact us about a Subscription Plan
            </button>
          </section>
        </div>

        <section className="---TeamSetupBilling-layout">
          <div className="---TeamSetupBilling-table-wrap">
            <table className="---TeamSetupBilling-table">
              <thead>
                <tr>
                  <th className="---TeamSetupBilling-feature-head">Compare Features</th>
                  {selectablePlans.map(([planKey, plan]) =>
                    renderPlanHeaderCell(planKey, plan)
                  )}
                </tr>
              </thead>

              <tbody>
                {FEATURE_ROWS.map((row) => (
                  <tr key={row.key}>
                    <th className="---TeamSetupBilling-row-label">{row.label}</th>
                    {selectablePlans.map(([planKey, plan]) => {
                      const selected = selectedPlanKey === planKey;

                      return (
                        <td
                          key={`${row.key}-${planKey}`}
                          className={`---TeamSetupBilling-cell ${selected ? "---TeamSetupBilling-cell--selected" : ""}`}
                        >
                          {getRowValue(row.key, planKey, plan)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {renderSummaryPanel()}
        </section>
      </div>

      </div>
    </TeamCenterFrame>
  );
};

export default TeamSetupBilling;
